<template>
  <key-multiselect
    ref='select'
    v-bind="$attrs"
    v-model="modelValueFromProps"
    :options="sortedOptions"
    :loading="loading"
    :autoPruneValues="false"
    :clearOnSelect="false"
    :preserveSearch="true"
    :internalSearch="internalSearch"
    :showLoadAndSelectAll="showLoadAndSelectAll"
    :fetchAllLimit="fetchAllLimit"
    :class="['remote-multiselect', {selected: !!modelValue}]"
    :style="{maxWidth}"
    @update:modelValue="onChange"
    @open="onOpen"
    @search-change="onSearchChange"
    @select="onSelect"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
    @item-added="onItemAdded"
    @fullValueChange="$emit('fullValueChange', $event)"
    @fetch-all-select-all="handleFetchAllSelectAll"
    @magnifying-glass-click="$emit('magnifying-glass-click', $event)"
  >
    <template #beforeList>
      <slot name="beforeList1" />
      <li class="limit-text" v-show="options.length >= limit">
        * More than {{ limit }} results found. Type some letters to narrow search criteria
        {{ infiniteScroll ? ' or scroll down to load more.' : '.' }}
      </li>
      <slot name="beforeList2" />
    </template>

    <template #afterList>
      <li v-if="loadingAll" class="infinite-scroll-status end">
        Loading all options...
      </li>
      <li v-if="showLoadingMore" class="infinite-scroll-status loading">
        <font-awesome-icon icon="circle-notch" spin />
        Loading more...
      </li>
      <li v-else-if="showEndOfList" class="infinite-scroll-status end">
        End of list
      </li>
      <slot name="afterList" />
    </template>

    <template v-for="(_, name) in passThruSlots" :key="name" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
  </key-multiselect>
</template>
<script>
import KeyMultiselect from './KeyMultiselect.vue'
import _ from 'lodash'
import { hasValue, forceArray } from '@/utils/misc'

export default {
  name: 'RemoteMultiselect',
  inheritAttrs: false,
  components: {
    KeyMultiselect
  },
  data () {
    return {
      // listLoaded indicates that we requested options from the server,
      // aside from the selected values. It hints to whether we should
      // make a load request when opening the widget.
      listLoaded: false,
      loading: false,
      // We want to allow loading model value and other values simultaneously.
      loadingModelValue: false,
      loadingOtherValues: false,
      loadingMore: false,
      loadingAll: false,
      options: [],
      // Store last search text so that we can refresh under certain circumstances.
      lastSearchText: null,
      nextCursor: null,
      scrollContainer: null,
    }
  },
  props: {
    id: {
      type: [Number, String]
    },
    /* TODO: Originally we set width (not max-width) to deal with select box
      being really wide on wide screens. But it's still too wide on
      narrow devices (phone), so we'll change it to max-width. I'm
      still not really sure if this is the right approach. 

      Previously, this is in the styles, but we put it here to allow it to be overridden by props.
      This is done to maximized spaces especially for multiselect that allows to load all options,
      which will make this component too long if width is not maximized
    */
    maxWidth: {
      type: String,
      default: '15rem'
    },
    modelValue: [Number, String, Array],
    limit: {
      type: Number,
      default: 25
    },
    serviceFetch: {
      // arguments: (searchText: string, value: number | string | array, limit: number)
      type: Function,
      required: true
    },
    internalSearch: {
      type: Boolean,
      default: false
    },
    infiniteScroll: {
      type: Boolean,
      default: false
    },
    // this is used to fetch all options from the backend and select them all
    showLoadAndSelectAll: {
      type: Boolean,
      default: false
    },
    fetchAllLimit: {
      type: Number,
      default: undefined
    },
    // clearOnSelect: {
    //   type: Boolean,
    //   default: true
    // },
    // preserveSearch: {
    //   type: Boolean,
    //   default: false
    // }
  },
  emits: ['fullValueChange', 'update:modelValue', 'options-changed', 'focus', 'blur', 'item-added', 'magnifying-glass-click'],
  computed: {
    sortedOptions () {
      return _.sortBy(this.options, item => item[this.$attrs.label])
    },
    passThruSlots () {
      return Object.fromEntries(Object.entries(this.$slots)
        .filter(([k, _v]) => !['beforeList', 'beforeList1', 'beforeList2', 'afterList'].includes(k))
      )
    },
    showLoadingMore () {
      return this.infiniteScroll && this.loadingMore
    },
    showEndOfList () {
      return this.infiniteScroll && this.listLoaded && !this.loadingMore && !this.nextCursor && this.options.length > 0
    },
    modelValueFromProps: {
      get () {
        return this.modelValue
      },
      set (newValue) {
        this.$emit('update:modelValue', newValue)
      }
    }
  },
  watch: {
    // If the newly-set value is not already loaded in the local list of options,
    // then we will remotely request it.
    modelValue: {
      handler (newValue, _oldValue) {
        const values = forceArray(newValue)
        const valuesNotLoaded = values.filter(v => !this.findOption(v, this.options))

        if (_.isEmpty(valuesNotLoaded)) {
          // no value set, or all values loaded
          return
        }

        // TODO: There is an edge case bug where an inactive option was loaded
        // TODO: for an existing value. Then, when the inactive value is removed,
        // TODO: either by user or by changing parent context, then inactve option
        // TODO: still remains in list. We should remove such inactive options once
        // TODO: they're no longer in the value set. This can be easlily tested with
        // TODO: labels.
        // TODO:   ==> Maybe we can keep two lists of options: one for model value
        // TODO:       and one for other values. A computed property can combine them
        // TODO:       to pass to KeyMultiselect (and de-dupe!). Then when model value changes,
        // TODO:       we can remove from any model value options that no longer exist in
        // TODO:       in model value.

        // Don't let loading this value prevent loading other values.
        this.loadingModelValue = true

        // Before we pass the value onto the child component, we need to download it
        // from server.
        this.search(null, valuesNotLoaded)
          .catch(() => {})
          .finally(() => { this.loadingModelValue = false })
      },
      immediate: true
    },
    id () {
      // Clear search text on VueMultiselect component
      // when parent id (e.g., model id) changes. We
      // need to do this, because we hard code preserveSearch
      // prop to true.
      const select = this.$refs.select?.$refs?.select
      if (select) select.search = ''

      // Reset options to only include those in model value.
      const modelValues = forceArray(this.modelValue)
      this.options = this.options
        .filter(option => modelValues.includes(this.optionValue(option)))
      // Reset list loaded, so that we'll load with new options when opened.
      this.listLoaded = false
      this.nextCursor = null
    },
    options (options) {
      this.$emit('options-changed', options)
    }
  },
  methods: {

    onChange (value) {
      // prevent infinite feedback loop
      if (!_.isEqual(value, this.modelValue)) {
        this.$emit('update:modelValue', value)
      }
    },

    onOpen () {
      // Be smart about only loading if necessary
      if (!this.listLoaded) {
        this.trySearch()
      }
      if (this.infiniteScroll) {
        this.$nextTick(() => this.bindScrollListener())
      }
    },

    onSelect () {
      // When the clearOnSelect prop is true, then selecting an option will
      // automatically clear the search field. We need to make sure in such
      // a case that we don't invoke a remote search. So the following boolean
      // flag prevents that from happening.
      this.ignoreNextSearchChange = true
    },

    onSearchChange (searchText) {
      // if (this.ignoreNextSearchChange) {
      //   this.ignoreNextSearchChange = false
      //   return
      // }
      if (this.internalSearch) return
      this.lastSearchText = searchText
      this.nextCursor = null
      this.debouncedSearch(searchText)
    },

    trySearch (searchText) {
      this.search(searchText).catch(error => {
        console.warn('Search failed', error)
      })
    },
    async handleFetchAllSelectAll () {
      this.onChange([]) // reset selected values
      this.options = [] // reset current options
      this.listLoaded = false
      this.nextCursor = null
      // Ensure selected options are loaded before we start loading all values
      await this.$nextTick()

      const requestLimit = this.fetchAllLimit || this.limit
      this.loadingAll = true

      try {
        await this.search(this.lastSearchText, null, null, false, requestLimit)

        console.log('nextCursor:', this.nextCursor)
        while (this.nextCursor) {
          console.log('Fetched another page of options for select all', { nextCursor: this.nextCursor })
          await this.search(this.lastSearchText, null, this.nextCursor, true, requestLimit)
        }

        this.onChange(_.uniq(this.options.map(option => this.optionValue(option))))
      } finally {
        this.loadingAll = false
      }
    },
    search (searchText, value, cursor = null, append = false, requestLimit = this.limit) {
      const loadingOtherValues = !value
      if (this.loading && loadingOtherValues && this.loadingOtherValues) return Promise.reject(new Error('Already loading'))

      this.loading = true
      if (loadingOtherValues) this.loadingOtherValues = true

      // We'll check if current value changed when result is received, in order to drop it.
      // We use this.modelValue instead of value, because we might not be searching the current value.
      const currentValue = this.modelValue

      return this.serviceFetch(searchText, value, requestLimit, cursor)
        .then(response => {
          const fetchedOptions = Array.isArray(response)
            ? response
            : (response?.results || [])
          const responseCursor = Array.isArray(response)
            ? null
            : (response?.cursor || null)
          this.nextCursor = responseCursor

          // TODO: If the following condition doesn't properly eliminate requests when the input
          // TODO: changes, then instead consider adding a prop for a model id.
          if (!_.isEqual(currentValue, this.modelValue) && (hasValue(currentValue) || hasValue(this.modelValue))) {
            return Promise.reject(
              new Error(`Value changed from "${currentValue}" to "${this.modelValue}", so this request no longer valid`))
          }

          const values = forceArray(this.modelValue)

          const baseOptions = append
            ? this.options
            : _.chain(this.options)
                // if loading other values, then filter out old options which are not in value set
                .filter(option => !loadingOtherValues || values.includes(this.optionValue(option)))
                .value()

          this.options = _.chain(baseOptions)
            .concat(fetchedOptions)
            .uniqBy(option => option.id)
            .value()

          if (!loadingOtherValues) {
            // If any values not found in options after request, then remove them.
            const valuesInOptions = values.filter(v => this.findOption(v, this.options))
            if (!_.isEqual(valuesInOptions, values)) {
              // console.log(`RemoteMultiSelect: values ${_.difference(values, ...valuesInOptions)} removed from list of options`)
              if (this.$attrs['multiple']) {
                this.onChange(valuesInOptions)
              } else {
                this.onChange(valuesInOptions.length > 0 ? valuesInOptions[0] : null)
              }
            }
          }

          this.listLoaded = this.listLoaded || !hasValue(value)

          if (searchText != this.lastSearchText) {
            console.log('Search text input was changed while loading. Run new fetch.')
            this.loading = false
            setTimeout(() => this.search(this.lastSearchText, value).catch(() => {}))
          }
        })
        .catch(error => {
          // TODO
          console.warn('failed to load selections', error)
        })
        .finally(() => {
          if (loadingOtherValues) this.loadingOtherValues = false
          this.loading = loadingOtherValues ? this.loadingModelValue : this.loadingOtherValues
        })
    },

    findOption (value, options) {
      return value ? options.find(option => this.optionValue(option) === value) : undefined
    },

    optionValue (option) {
      return option[this.$attrs['track-by']]
    },

    clear () {
      this.options = []
      this.listLoaded = false
      this.onChange(null)
    },

    // refresh may be called by parent via ref.
    refresh () {
      this.nextCursor = null
      this.trySearch(this.lastSearchText)
    },
    loadMore () {
      if (!this.infiniteScroll || !this.nextCursor || this.loading) return
      this.loadingMore = true
      this.search(this.lastSearchText, null, this.nextCursor, true)
        .catch(() => {})
        .finally(() => { this.loadingMore = false })
    },
    bindScrollListener () {
      const container = this.$refs.select?.$refs?.select?.$el?.querySelector('.multiselect__content-wrapper')
      if (!container || this.scrollContainer === container) return

      if (this.scrollContainer) {
        this.scrollContainer.removeEventListener('scroll', this.onDropdownScroll)
      }

      this.scrollContainer = container
      this.scrollContainer.addEventListener('scroll', this.onDropdownScroll)
    },
    onDropdownScroll (event) {
      const el = event.target
      if (!el) return
      const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight)
      if (distanceToBottom <= 24) {
        this.loadMore()
      }
    },
    onItemAdded (item) {
      this.$emit('update:modelValue', item.id)
      this.$emit('item-added', item)
    }
  },
  created () {
    // Debounce construction needs to be isolated per instance.
    this.debouncedSearch = _.debounce(function (searchText) {
      this.trySearch(searchText)
    }, 500)
  },
  beforeUnmount () {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.onDropdownScroll)
      this.scrollContainer = null
    }
  }
}
</script>
<style>
.remote-multiselect {
  .multiselect__content {

    max-width: 100%;

    .active > a {
      background-color: var(--legacy-theme-primary);
      color: #fff !important;
    }

    /* hover background */
    > .highlight > a {
      background-color: var(--legacy-theme-primary);
      color: #fff !important;
    }

    li {
      line-height: 2.5;

      > a {
        text-overflow: ellipsis;
        overflow-x: hidden;
      }
    }

    .limit-text {
      background-color: #F39C12;
      color: #fff;
      font-size: 12px;
      line-height: 1.2;
      padding: 10px;
      font-weight: bold;
    }

    .infinite-scroll-status {
      font-size: 14px;
      line-height: 1.2;
      padding: 10px;
      text-align: center;

      &.loading {
        color: var(--flat-ui-emerald);

        svg {
          margin-right: 0.4rem;
        }
      }

      &.end {
        color: #7f8c8d;
      }
    }
  }

  &.selected {
    input[type=search] {
      /* override the strange styling of this element to width:auto when
      value is selected. it causes a line break in the select box for
      long values. we must use width:0 instead of hiding the element,
      because it must be visible in order for the blur event to trigger. */
      width: 0 !important;
    }

    &.open {
      input[type=search] {
        display: inline-block;
      }
    }
  }

  .dropdown-toggle {
    display: block;
    overflow: hidden;
    white-space: nowrap;
  }

  .selected-tag {
    margin-bottom: 4px;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: calc(100% - 43px); /* subtract width of clear and dropdown icons */
  }
}
</style>
